import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, ArrowSquareOut, ArrowClockwise, Power, Television } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PrimaryButton, GhostButton } from "@/components/admin/parts";
import { adminApi, getErrorMessage } from "@/lib/api";
import { ConfirmAction, LabourSettings } from "./DisplayLinkParts";
import { timeAgo } from "./ccUtils";

async function copyText(text) {
  try { await navigator.clipboard.writeText(text); return true; } catch {
    const el = document.createElement("textarea");
    el.value = text; document.body.appendChild(el); el.select();
    const ok = document.execCommand("copy"); el.remove(); return ok;
  }
}

function LinkSection({ link, busy, run }) {
  if (!link.enabled) {
    return (
      <div className="rounded-xl border border-dashed border-neutral-800 p-5 text-center space-y-3" data-testid="display-link-empty">
        <Television size={30} weight="duotone" className="mx-auto text-neutral-600" />
        <p className="text-sm text-neutral-400">There's no TV link yet.</p>
        <PrimaryButton disabled={busy} onClick={() => run(() => adminApi.post("/admin/display-link"), "TV link created")} data-testid="display-link-create-btn">Create TV link</PrimaryButton>
      </div>
    );
  }
  const url = `${window.location.origin}/display/${link.token}`;
  return (
    <div className="space-y-3" data-testid="display-link-active">
      <textarea readOnly rows={2} value={url} onFocus={(e) => e.target.select()} data-testid="display-link-url"
        className="w-full resize-none break-all bg-black/40 border border-neutral-700 rounded-lg px-3 py-2 text-xs font-mono text-neutral-200 outline-none" />
      <div className="flex gap-2">
        <GhostButton className="!py-2" onClick={async () => (await copyText(url)) ? toast.success("Link copied") : toast.error("Couldn't copy, select the link and copy it")} data-testid="display-link-copy-btn"><Copy size={15} /> Copy</GhostButton>
        <a href={url} target="_blank" rel="noreferrer" data-testid="display-link-open-btn"
          className="inline-flex items-center gap-1.5 border border-neutral-700 text-neutral-200 rounded-full px-4 py-2 text-sm hover:border-neutral-500 hover:text-white transition-colors"><ArrowSquareOut size={15} /> Open</a>
      </div>
      <p className="text-xs text-neutral-500" data-testid="display-link-last-seen">
        Created {timeAgo(link.created_at)} · Last shown on a screen: {link.last_seen_at ? timeAgo(link.last_seen_at) : "not yet"}
      </p>
      <div className="flex gap-2">
        <ConfirmAction testid="display-link-reset" icon={ArrowClockwise} label="Reset link" busy={busy} title="Reset the TV link?"
          body="The current link stops working straight away on every screen. You'll need to open the new link on the TV."
          onConfirm={() => run(() => adminApi.post("/admin/display-link"), "New TV link created. The old one no longer works")} />
        <ConfirmAction testid="display-link-disable" icon={Power} label="Turn off link" busy={busy} danger title="Turn off the TV link?"
          body="Any screen showing the Command Centre will stop updating and show a 'link no longer valid' message. You can create a new link at any time."
          onConfirm={() => run(() => adminApi.post("/admin/display-link/disable"), "TV link turned off")} />
      </div>
    </div>
  );
}

export function DisplayLinkDialog({ open, onOpenChange, onSaved }) {
  const [link, setLink] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) adminApi.get("/admin/display-link").then(({ data }) => setLink(data)).catch((e) => toast.error(getErrorMessage(e)));
  }, [open]);
  const run = async (fn, msg) => {
    setBusy(true);
    try { const { data } = await fn(); setLink(data); toast.success(msg); onSaved?.(); } catch (e) { toast.error(getErrorMessage(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0F0F0F] border-neutral-800 text-neutral-100 sm:max-w-xl" data-testid="display-link-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">TV display link</DialogTitle>
          <DialogDescription className="text-neutral-500">
            A private, view-only link to this Command Centre. Open it in the TV's web browser and set the browser to full screen. It refreshes itself every 30 seconds and needs no keyboard or mouse. Anyone with the link can view it, so keep it private.
          </DialogDescription>
        </DialogHeader>
        {link ? (
          <div className="space-y-5">
            <LinkSection link={link} busy={busy} run={run} />
            <LabourSettings link={link} busy={busy} onSave={(body) => run(() => adminApi.put("/admin/command-centre/settings", body), "Labour settings saved")} />
          </div>
        ) : <p className="text-sm text-neutral-500" data-testid="display-link-loading">Loading…</p>}
      </DialogContent>
    </Dialog>
  );
}
